/**
 * The one switch for email, in Settings.
 *
 * It says what it will and will not send, because "email me" otherwise reads
 * as a promise to forward the changes feed — and the whole point of the rule
 * is that it doesn't. A date moving, firming up or being pulled stays in the
 * app, where "has anything moved?" is already answered.
 *
 * It shows the address as well as the switch: there is exactly one, it came
 * from signing in, and someone deciding whether to turn this on wants to know
 * where it would go without hunting for it.
 */

import { useEffect, useState } from 'react';
import { loadEmailReleases, setEmailReleases } from '../data.ts';

export function MailSetting({ userId, email }: { userId: string; email: string | null }) {
  const [on, setOn] = useState<boolean | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    loadEmailReleases(userId)
      .then((value) => live && setOn(value))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [userId]);

  async function flip() {
    if (on === null) return;
    const next = !on;
    // Moved first, put back if the write fails. A switch that waits for a
    // round trip before moving feels broken; one that moves and silently
    // didn't save is worse, which is what the notice is for.
    setOn(next);
    setFailed(false);
    try {
      await setEmailReleases(userId, next);
    } catch {
      setOn(!next);
      setFailed(true);
    }
  }

  return (
    <>
      <div className="section" style={{ marginTop: 28 }}>
        <div className="section-head">
          <span className="label">Email</span>
        </div>
      </div>
      <ul className="rows">
        <li>
          <div className="rowline">
            <span className="k">Tell me when something is out</span>
            <button
              type="button"
              role="switch"
              aria-checked={on ?? false}
              aria-label="Email me when something is out"
              disabled={on === null}
              className={on ? 'toggle on' : 'toggle'}
              onClick={() => void flip()}
            />
          </div>
          <p className="rowmeta">
            Only that. A date moving, firming up or being pulled stays in the app.
            {email ? ` Sent to ${email}.` : ''}
          </p>
        </li>
      </ul>
      {failed && <p className="notice">That didn’t save. Try again in a moment.</p>}
    </>
  );
}
